import React, { useState } from "react";
import { Combobox } from "@headlessui/react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import LoadingSpinner from "../LoadingSpinner";
import Checkbox from "../ui/checkbox";
import { OverflowDisplayTooltip } from "../ui/Tooltip/OverflowDisplayTooltip";

import { AutoCompleteOption } from "./types";

const MAX_VISIBLE_SELECTED = 3;

function getOptionClassName(active: boolean, selected: boolean, multi?: boolean): string {
  const rightPadding = !multi && selected ? "pr-9" : "pr-3";

  return `relative mx-1 flex cursor-pointer select-none items-center rounded-md py-2 pl-3 ${rightPadding} transition-colors ${
    active ? "bg-gray-100 text-gray-900" : "text-gray-900"
  }`;
}

interface AutoCompleteContentProps {
  filteredItems: AutoCompleteOption[];
  query: string;
  addNewOption?: boolean;
  isLoading?: boolean;
  getAddOptionText?: (query: string) => string;
  renderOption?: (option: AutoCompleteOption) => React.ReactNode;
  multi?: boolean;
  selectedValues?: string[];
  options?: AutoCompleteOption[];
}

const OptionItem = ({
  item,
  multi,
  selectedValues,
  renderOption,
}: {
  item: AutoCompleteOption;
  multi?: boolean;
  selectedValues: string[];
  renderOption?: (option: AutoCompleteOption) => React.ReactNode;
}) => {
  function renderOptionContent({
    active,
    selected,
  }: {
    active: boolean;
    selected: boolean;
  }): JSX.Element {
    return (
      <>
        {multi && (
          <Checkbox
            checked={selectedValues.includes(item.id) || selected}
            className="pointer-events-none mr-2"
          />
        )}
        <OverflowDisplayTooltip
          content={item?.label}
          className={`flex flex-1 items-center ${selected ? "font-medium" : "font-normal"}`}
        >
          <span>{renderOption?.(item) || item?.label}</span>
        </OverflowDisplayTooltip>
        {!multi && selected && (
          <span
            className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
              active ? "text-blue-600" : "text-blue-500"
            }`}
          >
            <Check className="h-4 w-4" />
          </span>
        )}
      </>
    );
  }

  return (
    <Combobox.Option
      value={item}
      className={({ active, selected }) => getOptionClassName(active, selected, multi)}
    >
      {renderOptionContent}
    </Combobox.Option>
  );
};

const SectionHeader = ({ title }: { title: string }) => (
  <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
    {title}
  </div>
);

const MultiSelectContent = ({
  filteredItems,
  query,
  addNewOption,
  getAddOptionText,
  renderOption,
  selectedValues,
  options,
}: Omit<AutoCompleteContentProps, "isLoading" | "multi"> & {
  selectedValues: string[];
  options: AutoCompleteOption[];
}) => {
  const [showAllSelected, setShowAllSelected] = useState(false);

  const selectedOptions = selectedValues.map(
    v => options.find(o => o.id === v) ?? { id: v, value: v, label: v },
  );

  const visibleSelected = showAllSelected
    ? selectedOptions
    : selectedOptions.slice(0, MAX_VISIBLE_SELECTED);
  const hasMoreSelected = selectedOptions.length > MAX_VISIBLE_SELECTED;

  const resultItems = filteredItems.filter(item => !selectedValues.includes(item.id));

  const hasExactMatch = filteredItems.some(item => item.label === query);
  const showCreateOption = addNewOption && query.trim() !== "" && !hasExactMatch;

  function toggleShowAllSelected(): void {
    setShowAllSelected(prev => !prev);
  }

  function preventMouseDownDefault(e: React.MouseEvent<HTMLButtonElement>): void {
    e.preventDefault();
  }

  return (
    <>
      {selectedOptions.length > 0 && (
        <>
          <SectionHeader title="Selected" />
          {visibleSelected.map(item => (
            <OptionItem
              key={item.id}
              item={item}
              multi
              selectedValues={selectedValues}
              renderOption={renderOption}
            />
          ))}
          {hasMoreSelected && (
            <button
              type="button"
              onMouseDown={preventMouseDownDefault}
              onClick={toggleShowAllSelected}
              className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-1 rounded-md px-3 py-1.5 text-xs text-blue-600 hover:bg-gray-50"
            >
              {showAllSelected ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />
                  Show all ({selectedOptions.length})
                </>
              )}
            </button>
          )}
          <div className="mx-2 my-1 border-t border-gray-200" />
        </>
      )}

      <SectionHeader title="Results" />

      {showCreateOption && (
        <Combobox.Option
          key="__create_new__"
          value={{ id: query.trim(), value: query.trim(), label: query.trim() }}
          className={({ active, selected }) => getOptionClassName(active, selected, true)}
        >
          <>
            <Checkbox checked={false} className="pointer-events-none mr-2" />
            {getAddOptionText?.(query.trim()) || `Create "${query.trim()}"`}
          </>
        </Combobox.Option>
      )}

      {resultItems.length > 0 ? (
        resultItems.map(item => (
          <OptionItem
            key={item.id}
            item={item}
            multi
            selectedValues={selectedValues}
            renderOption={renderOption}
          />
        ))
      ) : (
        <div className="relative py-2 pl-3 pr-4 text-gray-500">
          {query ? `No matches found for "${query}".` : "No more options available"}
        </div>
      )}
    </>
  );
};

export const AutoCompleteContent = ({
  filteredItems,
  query,
  addNewOption,
  isLoading,
  getAddOptionText,
  renderOption,
  multi,
  selectedValues = [],
  options = [],
}: AutoCompleteContentProps) => {
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner size={4} />
      </div>
    );
  }

  if (multi) {
    return (
      <MultiSelectContent
        filteredItems={filteredItems}
        query={query}
        addNewOption={addNewOption}
        getAddOptionText={getAddOptionText}
        renderOption={renderOption}
        selectedValues={selectedValues}
        options={options}
      />
    );
  }

  if (filteredItems.length === 0 && query === "") {
    return <div className="relative py-2 pl-3 pr-4 text-gray-500">No matches found</div>;
  }

  const hasExactMatch = filteredItems.some(item => item.label === query);
  const showCreateOption = addNewOption && query.trim() !== "" && !hasExactMatch;

  if (filteredItems.length === 0 && query !== "") {
    if (addNewOption) {
      return (
        <Combobox.Option
          className="relative flex cursor-pointer select-none items-center py-2 pl-3 pr-4 text-gray-900 transition-colors hover:bg-gray-50"
          value={{ id: query.trim(), value: query.trim(), label: query.trim() }}
        >
          <>
            {getAddOptionText?.(query.trim()) ||
              `Would you like to add ${query.trim()} as a new option?`}
          </>
        </Combobox.Option>
      );
    }

    return (
      <div className="relative py-2 pl-3 pr-4 text-gray-500">
        No matches found for &ldquo;{query}&rdquo;.
      </div>
    );
  }

  return (
    <>
      {showCreateOption && (
        <Combobox.Option
          key="__create_new__"
          value={{ id: query.trim(), value: query.trim(), label: query.trim() }}
          className={({ active, selected }) => getOptionClassName(active, selected)}
        >
          <>{getAddOptionText?.(query.trim()) || `Create "${query.trim()}"`}</>
        </Combobox.Option>
      )}
      {filteredItems.map(item => (
        <OptionItem
          key={item.id}
          item={item}
          selectedValues={selectedValues}
          renderOption={renderOption}
        />
      ))}
    </>
  );
};
